// @flow
import resourceFactory from 'app/common/lib/ResourceFactory';
import type { Dispatch } from 'redux';
import {
  FEMALE,
  MALE,
  UNSPECIFIED,
} from 'app/common/react/components/growth/activation/NuxConst/NuxConst';

export type updateUserProps = {
  updateUserData: $Shape<{
    gender: ?string,
    custom_gender: ?string,
    age: ?string,
    birthdate: ?string,
    locale: ?string,
    country: ?string,
    first_name: ?string,
    last_name: ?string,
    full_name: ?string,
    parent_email: ?string,
    website_url: ?string,
    advertising_intent: ?number,
    business_name: ?string,
    account_type: ?string,
    business_goals: ?(string[]),
    enable_profile_message: ?boolean,
    enable_profile_address: ?boolean,
  }>,
};

type UpdatePartnerPayloadType = {
  account_type?: string,
  advertising_intent?: ?number,
  business_goals: ?(string[]),
  business_name?: string,
  created_at?: ?string,
  is_convert?: boolean,
  is_create?: boolean,
  is_linked_business?: boolean,
  enable_profile_message?: boolean,
  enable_profile_address?: boolean,
  contact_name?: string,
  contact_email?: string,
  contact_phone?: string,
  contact_phone_country?: {
    code?: string,
    phone_code?: string,
  },
  profile_place?: {
    street?: string,
    extra_street?: string,
    locality?: string,
    region?: string,
    postal_code?: string,
    country?: string,
    longitude?: number,
    latitude?: number,
  },
};

export type UpdateUserPayloadType = {
  type: 'UPDATE_USER_SET',
  payload: {
    gender?: string,
    customGender?: string,
    age?: string,
    birthdate?: string,
    locale?: string,
    country?: string,
    firstName?: string,
    lastName?: string,
    fullName?: string,
    parentEmail?: string,
    websiteUrl?: string,
    partner?: UpdatePartnerPayloadType,
  },
};

export function updateUser(props: updateUserProps) {
  const { updateUserData } = props;
  const updateUserPayload = {};
  let updatePartnerPayload = null;

  if (updateUserData.gender && [FEMALE, MALE].includes(updateUserData.gender)) {
    updateUserPayload.gender = updateUserData.gender;
  } else if (updateUserData.custom_gender) {
    updateUserPayload.customGender = updateUserData.custom_gender;
    updateUserPayload.gender = UNSPECIFIED;
  }

  if (updateUserData.age) {
    updateUserPayload.age = updateUserData.age;
  } else if (updateUserData.birthdate) {
    updateUserPayload.birthdate = updateUserData.birthdate;
  }

  if (updateUserData.locale) {
    updateUserPayload.locale = updateUserData.locale;
  }
  if (updateUserData.country) {
    updateUserPayload.country = updateUserData.country;
  }

  if (updateUserData.first_name) {
    updateUserPayload.firstName = updateUserData.first_name;
  }
  if (updateUserData.last_name) {
    updateUserPayload.lastName = updateUserData.last_name;
  }
  if (updateUserData.full_name) {
    updateUserPayload.fullName = updateUserData.full_name;
  }
  if (updateUserData.parent_email) {
    updateUserPayload.parentEmail = updateUserData.parent_email;
  }
  if (updateUserData.website_url) {
    updateUserPayload.websiteUrl = updateUserData.website_url;
  }

  if (
    updateUserData.advertising_intent ||
    updateUserData.business_name ||
    updateUserData.account_type ||
    updateUserData.business_goals ||
    updateUserData.enable_profile_message ||
    updateUserData.enable_profile_address
  ) {
    const {
      account_type,
      advertising_intent,
      business_name,
      business_goals,
      enable_profile_message,
      enable_profile_address,
    } = updateUserData;
    updatePartnerPayload = {
      ...(advertising_intent ? { advertising_intent } : {}),
      ...(account_type ? { account_type } : {}),
      ...(business_name ? { business_name } : {}),
      ...(business_goals ? { business_goals } : {}),
      ...(enable_profile_message ? { enable_profile_message } : {}),
      ...(enable_profile_address ? { enable_profile_address } : {}),
    };
  }

  if (updatePartnerPayload) {
    updateUserPayload.partner = updatePartnerPayload;
    // Ensures we update the header profile button immediately after creating the business account
    if (updatePartnerPayload.business_name) {
      // Ensure firstName is updated only when business_name is updated so firstName is never null
      updateUserPayload.firstName = updatePartnerPayload.business_name;
    }
  }

  const updateUserPromise = resourceFactory
    .create('UserSettingsResource', updateUserData)
    .callUpdate();

  return function(dispatch: (UpdateUserPayloadType) => void, getState: () => void) {
    return updateUserPromise.then((updatedUser) => {
      dispatch({
        type: 'UPDATE_USER_SET',
        payload: updateUserPayload,
      });
      return updatedUser;
    });
  };
}

export type UpdateUserFirstHomeFeedRequestAfterNux = {|
  type: 'UPDATE_USER_FIRST_HOME_FEED_REQUEST_AFTER_NUX',
  payload: { firstHomeFeedRequestAfterNux: boolean },
|};

export function updateFirstHomeFeedRequestAfterNuxFlag(
  enabled: boolean,
): UpdateUserFirstHomeFeedRequestAfterNux {
  return {
    type: 'UPDATE_USER_FIRST_HOME_FEED_REQUEST_AFTER_NUX',
    payload: {
      firstHomeFeedRequestAfterNux: enabled,
    },
  };
}

export function setUserState(userState: string, value: number) {
  return (dispatch: Dispatch<any>) => {
    const userStateResource = resourceFactory.create('UserStateResource', {
      state: userState,
      value,
    });
    userStateResource.callCreate();
  };
}

export function updateFollowedInterests(interestFollowingMapping: {
  [interestId: number]: boolean,
}) {
  return (dispatch: Dispatch<any>) =>
    resourceFactory
      .create('UpdateFollowedInterestsResource', {
        updated_interest_follows: interestFollowingMapping,
      })
      .callUpdate();
}

export function submitOrientationContextFollow() {
  return (dispatch: Dispatch<any>) =>
    resourceFactory
      .create('OrientationContextResource', {
        follow_urss_topics: true,
      })
      .callCreate();
}

export function submitOrientationContextData(data: Object) {
  return (dispatch: Dispatch<any>) =>
    resourceFactory.create('OrientationContextResource', data).callCreate();
}
